// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@conklin/App/Widgets/DesignSystem/DesignSystem.helpers";
import { dynamicDropdownOptions, longText } from "@conklin/App/Widgets/DesignSystem/DummyData/DummyData";
import { TextFieldPresentationProps } from "@conklin/MobiusOverrides/TextField/TextField";
import { searchBoxFieldClickableStyles, xValidateOnSubmit } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import DatePicker from "@insite/mobius/DatePicker";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import styled, { css } from "styled-components";

interface FormTabStyles {
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    columnFormWrapper?: InjectableCss;
    searchField?: TextFieldPresentationProps;
}

const formTabStyles: FormTabStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
            align-content: flex-start;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
            flex-wrap: wrap;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
    columnFormWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-top: 20px;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
    searchField: {
        ...searchBoxFieldClickableStyles,
    },
};

const styles = formTabStyles;
const FormTab: FC = () => {
    const [radioValue, setRadioValue] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");

    const onRadioChangeHandler = (e: any) => {
        setRadioValue(e.target.value);
    };

    return (
        <SectionContainer>
            {/* <XNG-Changes: Input -------------------------------------------------- */}
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Form : Title + Input / Error</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        label="User Name"
                        required
                        data-test-selector="userName"
                        placeholder="Placeholder"
                        type="userName"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        label="User Name"
                        required={false}
                        data-test-selector="userName"
                        placeholder="Placeholder"
                        type="userName"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        label="User Name - Disabled"
                        disabled
                        data-test-selector="userName"
                        placeholder="Placeholder"
                        type="userName"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField label="User Name - Error" error="Error" data-test-selector="userName" type="userName" />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        label="User Name - Small"
                        data-test-selector="userName"
                        type="userName"
                        sizeVariant="small"
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Form : No Title</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField required data-test-selector="userName" placeholder="Placeholder" type="userName" />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField disabled data-test-selector="userName" placeholder="Placeholder" type="userName" />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        disabled
                        data-test-selector="userName"
                        placeholder="Placeholder"
                        type="userName"
                        sizeVariant="small"
                    />
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: Input ------------------------------------------------- */}
            {/* <XNG-Changes: Input Search ------------------------------------------- */}
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Search</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        data-test-selector="search"
                        placeholder="Search by Keyword"
                        iconClickableProps={{ onClick: () => {} }}
                        {...styles.searchField}
                        {...{ showClear: true }}
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.currentTarget.value)}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        data-test-selector="search"
                        placeholder="Search by Keyword"
                        iconClickableProps={{ onClick: () => {} }}
                        {...styles.searchField}
                        disabled
                        {...{ showClear: true }}
                    />
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: Input Search ------------------------------------------ */}
            {/* <XNG-Changes: TextArea ----------------------------------------------- */}
            <GridItem {...styles.gridItem}>
                <SectionTitle>TextArea</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextArea placeholder="Type some content" required label="Description - Required" />
                    <TextArea placeholder="Type some content" defaultValue={longText} label="Description - Long Text" />
                    <TextArea
                        placeholder="Type some content"
                        defaultValue={longText}
                        disabled
                        label="Description - Disabled"
                    />
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: TextArea ----------------------------------------------- */}
            {/* <XNG-Changes: Select -------------------------------------------------- */}
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Drop Down</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Select label="Drop Down Title" required placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month" disabled>
                            {translate("Month")}
                        </option>
                        <option value="Year">{translate("Year")}</option>
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <Select label="Drop Down Title" disabled placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                        <option value="Year">{translate("Year")}</option>
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <Select
                        label="Small Drop Down Title"
                        placeholder="Select"
                        sizeVariant="small"
                        onChange={event => {}}
                    >
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                        <option value="Year">{translate("Year")}</option>
                    </Select>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Drop Down : No Title</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Select required placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                        <option value="Year">{translate("Year")}</option>
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <Select disabled placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month" disabled>
                            {translate("Month")}
                        </option>
                        <option value="Year">{translate("Year")}</option>
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <Select placeholder="Select" sizeVariant="small" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month" disabled>
                            {translate("Month")}
                        </option>
                        <option value="Year">{translate("Year")}</option>
                    </Select>
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: Select ------------------------------------------------- */}
            {/* <XNG-Changes: Dynamic Dropdown ---------------------------------------- */}
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Dynamic Dropdown</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        label="Dynamic Dropdown"
                        onSelectionChange={(value?: string) => {}}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        label="Dynamic Dropdown"
                        onSelectionChange={(value?: string) => {}}
                        disabled
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        label="Small Dynamic Dropdown"
                        onSelectionChange={(value?: string) => {}}
                        sizeVariant="small"
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Input : Dynamic Dropdown</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown options={dynamicDropdownOptions} onSelectionChange={(value?: string) => {}} />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        onSelectionChange={(value?: string) => {}}
                        disabled
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        onSelectionChange={(value?: string) => {}}
                        sizeVariant="small"
                    />
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: Dynamic Dropdown --------------------------------------- */}
            {/* <XNG-Changes: Date Picker --------------------------------------------- */}
            <GridItem {...styles.gridItem}>
                <SectionTitle>DatePicker</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DatePicker label="Date" />
                    <DatePicker label="Date (required)" required />
                    <DatePicker label="Date (selected)" selectedDay={new Date(2019, 8, 12)} />
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: Date Picker -------------------------------------------- */}

            {/* <XNG-Changes: Checkbox ------------------------------------------------ */}
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Checkbox</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <CheckboxGroup label="Checkbox Group Label">
                        <Checkbox checked>Checked</Checkbox>
                        <Checkbox>Unchecked</Checkbox>
                        <Checkbox checked disabled>
                            Disabled Checked
                        </Checkbox>
                        <Checkbox checked="indeterminate" disabled>
                            Disabled Indeterminate
                        </Checkbox>
                        <Checkbox disabled>Disabled</Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Checkbox</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <CheckboxGroup label="Checkbox Group Label">
                        <Checkbox variant="toggle" checked>
                            Checked
                        </Checkbox>
                        <Checkbox variant="toggle">Unchecked</Checkbox>
                        <Checkbox variant="toggle" checked disabled>
                            Disabled Checked
                        </Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
            </GridItem>
            {/* </XNG-Changes: Checkbox ----------------------------------------------- */}
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Radio</SectionTitle>
                <StyledWrapper {...styles.columnFormWrapper}>
                    <RadioGroup value={radioValue} onChangeHandler={onRadioChangeHandler}>
                        <Radio key="All">All</Radio>
                        <Radio key="Unread">Unread</Radio>
                        <Radio key="Read">Read</Radio>
                        <Radio key="Disabled" disabled={true}>
                            Disabled
                        </Radio>
                    </RadioGroup>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.gridItem}>
                <SectionTitle>Form Validation</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <FormExample />
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default FormTab;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormExample = () => {
    const formId = "myFormId";
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");

    const handleEmailChange = (e: any) => {
        setEmail(e.target.value);
    };

    const handleMessageChange = (e: any) => {
        setMessage(e.target.value);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const errors = xValidateOnSubmit(formId);
        // eslint-disable-next-line no-console
        console.log({ errors });
        if (errors.length > 0) {
            return;
        }
        // eslint-disable-next-line no-console
        console.log("Submitted");
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
            <TextField
                label={translate("Email")}
                required={true}
                onChange={handleEmailChange}
                value={email}
                type="email"
                {...{ formId }}
            />

            <TextArea
                label={translate("Your Message")}
                onChange={handleMessageChange}
                required={true}
                value={message}
                {...{ formId }}
            />
            <Button type="submit">{translate("send")}</Button>
        </StyledForm>
    );
};
